/**
 * Generator 3: React Query hook — admin/hooks/use-<n>.ts
 * Generates client-side data fetching hooks wrapping server actions
 */

import { flattenFields } from '@core-engine/schema/field-helpers/index.js'
import type { GeneratedFile, GeneratorOptions, Schema } from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import { pluralize, singularize, toCamelCase, toPascalCase } from '@core-engine/utils/string.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths.js'
import { resolveEntityFilters } from '@next-adapter/generators/entity-filters.js'

// ============================================================================
// Public API
// ============================================================================

export interface HookGeneratorResult {
  files: GeneratedFile[]
  hookName: string
}

/**
 * Generate React Query hook for an entity — writes to admin/hooks/use-<n>.ts
 */
export function generateHook(
  schema: Schema,
  hooksDir: string,
  options: GeneratorOptions = {}
): HookGeneratorResult {
  const hookFileName = `use-${schema.name}.ts`
  const actionImportPath = resolveSchemaActionImportPath(schema.name, options.outputNamespace)

  const singular = singularize(schema.name)
  const plural = pluralize(schema.name)
  const Singular = toPascalCase(singular)
  const Plural = toPascalCase(plural)

  // Check if schema has a slug field
  const dbFields = flattenFields(schema.fields)
  const hasSlugField = dbFields.some((f) => f.name === 'slug')

  const resolvedFilters = resolveEntityFilters(schema).map((filter) => ({
    ...filter,
    variableName: toCamelCase(filter.key),
    pascalName: toPascalCase(filter.key),
    fromVariableName: `${toCamelCase(filter.key)}From`,
    toVariableName: `${toCamelCase(filter.key)}To`
  }))
  const hasFilters = resolvedFilters.length > 0

  // --- Build imports from actions ---
  const actionImports: string[] = [`get${Plural}`, `get${Singular}ById`]
  if (hasSlugField) actionImports.push(`get${Singular}BySlug`)
  if (hasFilters) {
    for (const filter of resolvedFilters) {
      if (filter.source === 'distinct') {
        actionImports.push(`getDistinct${Plural}${filter.pascalName}`)
      }
    }
  }
  const typeImports = [`type ${Singular}`, `type ${Plural}Page`, `type ${Plural}Query`]

  // --- Build filter parameters ---
  const filterParams = hasFilters
    ? resolvedFilters
        .flatMap((filter) =>
          filter.type === 'date-range'
            ? [`${filter.fromVariableName}?: string`, `${filter.toVariableName}?: string`]
            : [`${filter.variableName}?: string`]
        )
        .join(', ')
    : ''
  const paginationParams =
    "page?: number, pageSize?: number, orderBy?: string, orderDirection?: 'asc' | 'desc'"
  const allParams = filterParams
    ? `search?: string, ${filterParams}, ${paginationParams}`
    : `search?: string, ${paginationParams}`

  // --- Build query key with all filter params + pagination ---
  const queryKeyParts = [
    "search ?? ''",
    ...(hasFilters
      ? resolvedFilters.flatMap((filter) =>
          filter.type === 'date-range'
            ? [`${filter.fromVariableName} ?? ''`, `${filter.toVariableName} ?? ''`]
            : [`${filter.variableName} ?? ''`]
        )
      : []),
    'safePage',
    'safePageSize',
    "orderBy ?? ''",
    "orderDirection ?? ''"
  ].join(', ')

  // --- Build filters object in queryFn ---
  const paginationBlock = `      if (safePageSize !== -1) {
        filters.limit = safePageSize
        filters.offset = safePage * safePageSize
      }
      if (orderBy) filters.orderBy = orderBy
      if (orderDirection) filters.orderDirection = orderDirection`

  const filtersBody = hasFilters
    ? `const filters: ${Plural}Query = {}
      if (search) filters.search = search
${resolvedFilters
  .map((filter) => {
    if (filter.builtIn === 'status') {
      return `      if (status === 'published') filters.published = true
      if (status === 'draft') filters.published = false`
    }

    if (filter.type === 'date-range') {
      return `      if (${filter.fromVariableName}) filters.${filter.field}From = ${filter.fromVariableName}
      if (${filter.toVariableName}) filters.${filter.field}To = ${filter.toVariableName}`
    }

    if (filter.type === 'boolean') {
      return `      if (${filter.variableName} === 'true') filters.${filter.field} = true
      if (${filter.variableName} === 'false') filters.${filter.field} = false`
    }

    return `      if (${filter.variableName}) filters.${filter.field} = ${filter.variableName}`
  })
  .join('\n')}
${paginationBlock}
      return get${Plural}(Object.keys(filters).length > 0 ? filters : undefined)`
    : `const filters: ${Plural}Query = {}
      if (search) filters.search = search
${paginationBlock}
      return get${Plural}(Object.keys(filters).length > 0 ? filters : undefined)`

  // --- Singular hook ---
  const singularHook = `
export function use${Singular}(id: string | null | undefined): UseQueryResult<${Singular} | null, Error> {
  return useQuery({
    queryKey: ['${singular}', id],
    queryFn: () => (id ? get${Singular}ById(id) : Promise.resolve(null)),
    enabled: !!id,
  })
}`

  // --- Slug hook ---
  const slugHook = hasSlugField
    ? `

export function use${Singular}BySlug(slug: string | null | undefined): UseQueryResult<${Singular} | null, Error> {
  return useQuery({
    queryKey: ['${singular}', 'slug', slug],
    queryFn: () => (slug ? get${Singular}BySlug(slug) : Promise.resolve(null)),
    enabled: !!slug,
  })
}`
    : ''

  // --- Distinct filter hooks ---
  const distinctHooks = hasFilters
    ? resolvedFilters
        .filter((filter) => filter.source === 'distinct')
        .map(
          (filter) => `

export function use${Plural}Distinct${filter.pascalName}(): UseQueryResult<string[], Error> {
  return useQuery({
    queryKey: ['${plural}', 'distinct', '${filter.key}'],
    queryFn: () => getDistinct${Plural}${filter.pascalName}()
  })
}`
        )
        .join('')
    : ''

  // --- Assemble file ---
  const content = `import {
  ${[...actionImports, ...typeImports].join(',\n  ')}
} from '@admin/actions/${actionImportPath}'
import { type UseQueryResult, useQuery } from '@tanstack/react-query'

export function use${Plural}(
  ${allParams ? `${allParams},` : ''}
  options?: { enabled?: boolean }
): UseQueryResult<${Plural}Page, Error> {
  const safePage = Number.isFinite(page ?? 0) && (page ?? 0) > 0 ? (page ?? 0) : 0
  const safePageSize = pageSize === -1 || (Number.isFinite(pageSize ?? 20) && (pageSize ?? 20) > 0) ? (pageSize ?? 20) : 20

  return useQuery({
    queryKey: ['${plural}', ${queryKeyParts}],
    queryFn: () => {
      ${filtersBody}
    },
    enabled: options?.enabled ?? true,
  })
}
${singularHook}${slugHook}${distinctHooks}
`

  return {
    files: [createGeneratedFile(`${hooksDir}/${hookFileName}`, content)],
    hookName: `use${Plural}`
  }
}

/**
 * Generate React Query hook for a single (singleton) schema.
 * Only useXxx() query — no list, slug, or filter hooks.
 */
export function generateSingleHook(
  schema: Schema,
  hooksDir: string,
  options: GeneratorOptions = {}
): HookGeneratorResult {
  const hookFileName = `use-${schema.name}.ts`
  const actionImportPath = resolveSchemaActionImportPath(schema.name, options.outputNamespace)

  const singular = singularize(schema.name)
  const Singular = toPascalCase(singular)

  const content = `import {
  get${Singular},
  type ${Singular}
} from '@admin/actions/${actionImportPath}'
import { type UseQueryResult, useQuery } from '@tanstack/react-query'

interface Use${Singular}Options {
  initialData?: ${Singular} | null
}

export function use${Singular}(options: Use${Singular}Options = {}): UseQueryResult<${Singular} | null, Error> {
  return useQuery({
    queryKey: ['${schema.name}'],
    queryFn: () => get${Singular}(),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    ...(options.initialData !== undefined ? { initialData: options.initialData } : {}),
  })
}
`

  return {
    files: [createGeneratedFile(`${hooksDir}/${hookFileName}`, content)],
    hookName: `use${Singular}`
  }
}
